import * as T from 'three';
import type { Simulation } from './simulation';
import type { MoveInput, SimState, Traversal, Vec3, WorldData } from './types';
import { branchTaper, branchStyle, spanCurve } from './branch-shape';

export type CatchPreview = { id: number; point: Vec3; path: Vec3[] };

/** A prospective release/reach using an isolated copy of the shipping controller. */
export function previewCatch(sim: Simulation, world: WorldData, state: SimState, input: MoveInput): CatchPreview | null {
    if (state.mode === 4 || state.mode === 5) return null;
    const initial = [...state.raw];
    if (state.mode === 0) initial[17] &= ~5; // Preview a fresh jump press.
    sim.restore(initial, 27);
    const path: Vec3[] = [[...state.p]];
    for (let i = 0; i < 192; i++) {
        const release = state.grab >= 0 && i < 24;
        sim.step(27, { ...input, buttons: release ? (i === 0 ? 16 : 0) : (input.buttons & 2 ? 2 : 5) });
        const next = sim.read(27);
        if (i % 6 === 0) path.push(next.p);
        if (next.grab >= 0 && next.grab !== state.grab) {
            const o = world.objects.find(o => o.id === next.grab);
            if (!o) return null;
            const point = o.a.map((n, axis) => n + (o.b[axis] - n) * next.gripT) as Vec3;
            if (o.type === 3) point[1] += next.flex;
            path.push(next.p);
            return { id: next.grab, point, path };
        }
        if (next.mode === 4 || (i > 8 && next.mode === 0)) break;
    }
    return null;
}

export class TraversalGuide {
    preview: CatchPreview | null = null;
    private clock = 0;
    private previousGrab = -2;
    private ring = new T.Mesh(new T.TorusGeometry(.29, .045, 6, 32), new T.MeshBasicMaterial({ color: '#c2ffe1', depthTest: true }));
    private line = new T.Line(new T.BufferGeometry(), new T.LineDashedMaterial({ color: '#f6d47c', dashSize: .22, gapSize: .18, transparent: true, opacity: .7 }));
    private label = document.getElementById('catch-target')!;
    private point: Vec3 | null = null;
    /**
     * Contextual highlight: a soft halo shell around only the catchable span of the best candidate
     * (mint = in reach now, gold = the advertised next catch). Back faces of a slightly larger tube
     * read as a rim/outline without any transparency sorting cost on the branch itself.
     */
    private halo = new T.Mesh(new T.BufferGeometry(), new T.MeshBasicMaterial({ color: '#8dffc8', side: T.BackSide, transparent: true, opacity: .5, depthWrite: false, fog: false, toneMapped: false }));
    private haloKey = '';
    private glow = 0;
    constructor(private sim: Simulation, private world: WorldData, scene: T.Scene) {
        scene.add(this.ring, this.line, this.halo);
        this.ring.visible = this.line.visible = this.halo.visible = false;
        this.halo.renderOrder = 2;
    }
    private highlight(o: Traversal | undefined, inReach: boolean, dt: number) {
        this.glow = o ? Math.min(1, this.glow + dt * 6) : 0;
        this.halo.visible = !!o;
        if (!o) return;
        const key = `${o.id}:${o.type}:${o.a}:${o.b}:${o.radius}`;
        if (key !== this.haloKey) {
            this.haloKey = key;
            this.halo.geometry.dispose();
            const thick = o.type === 1 || o.type === 7 ? o.radius * branchTaper(branchStyle(o))[0] : Math.max(o.radius, .07) * 1.1;
            const length = Math.hypot(o.b[0] - o.a[0], o.b[1] - o.a[1], o.b[2] - o.a[2]);
            this.halo.geometry = new T.TubeGeometry(spanCurve(o), Math.max(8, Math.ceil(length * 3)), thick + .1, 10, false);
        }
        const pulse = .5 + .5 * Math.sin(performance.now() * .006);
        this.halo.material.color.set(inReach ? '#8dffc8' : '#ffd36a');
        this.halo.material.opacity = this.glow * (inReach ? .62 + pulse * .25 : .36 + pulse * .16);
    }
    update(dt: number, state: SimState, input: MoveInput, camera: T.Camera, active: boolean) {
        this.ring.visible = this.line.visible = false;
        this.label.hidden = true;
        if (!active || state.mode === 4) { this.preview = null; this.clock = 0; this.highlight(undefined, false, dt); return; }
        this.clock -= dt;
        if (this.clock <= 0 || state.grab !== this.previousGrab) {
            this.clock = .12;
            this.previousGrab = state.grab;
            this.preview = previewCatch(this.sim, this.world, state, input);
            this.line.geometry.dispose();
            this.line.geometry = new T.BufferGeometry().setFromPoints((this.preview?.path || []).map(p => new T.Vector3(...p)));
            this.line.computeLineDistances();
        }
        // An in-reach marker comes from the solver's visible, unblocked candidate.
        const candidate = state.grab < 0 ? this.world.objects.find(o => o.id === state.candidate) : undefined;
        if (candidate) {
            const a = new T.Vector3(...candidate.a), b = new T.Vector3(...candidate.b);
            const p = new T.Vector3(...state.p).add(new T.Vector3(input.x, 0, input.z).multiplyScalar(.35));
            this.point = new T.Line3(a, b).closestPointToPoint(p, true, new T.Vector3()).toArray() as Vec3;
        } else this.point = this.preview?.point || null;
        const focus = candidate || (this.preview && this.preview.id !== state.grab ? this.world.objects.find(o => o.id === this.preview!.id) : undefined);
        this.highlight(focus && [1, 2, 3, 7].includes(focus.type) ? focus : undefined, !!candidate, dt);
        let approachDistance = 0;
        if (!this.point && state.mode === 0) {
            let nearest = 12;
            for (const o of this.world.objects) {
                if (![1, 2, 3, 7].includes(o.type)) continue;
                const p = new T.Line3(new T.Vector3(...o.a), new T.Vector3(...o.b)).closestPointToPoint(new T.Vector3(...state.p), true, new T.Vector3());
                const delta = p.clone().sub(new T.Vector3(...state.p));
                const d = delta.length();
                if (d < nearest && delta.y > 0 && delta.x * input.x + delta.z * input.z > 0) {
                    nearest = d; this.point = p.toArray() as Vec3; approachDistance = d;
                }
            }
        }
        if (!this.point) return;
        this.ring.visible = true;
        this.ring.position.set(...this.point);
        this.ring.quaternion.copy(camera.quaternion);
        this.ring.material.color.set(candidate ? '#c2ffe1' : '#f6d47c');
        this.line.visible = !!this.preview && state.grab >= 0;
        const p = this.ring.position.clone().project(camera);
        if (p.z < -1 || p.z > 1 || Math.abs(p.x) > .86 || Math.abs(p.y) > .8) return;
        this.label.hidden = false;
        this.label.textContent = candidate ? 'In reach · hold to catch' : approachDistance ? `Move closer · ${Math.ceil(approachDistance)} m` : 'Next catch';
        this.label.style.left = `${(p.x * .5 + .5) * innerWidth}px`;
        this.label.style.top = `${(-p.y * .5 + .5) * innerHeight - 25}px`;
    }
}
